import { z } from 'zod';
import {
  ACTIVITY_TEMPLATES,
  ACTIVITY_TEMPLATE_KEYS,
  type ActivityTemplateKey,
} from './activity';
import type { EventType } from './events';
import { LOCALE_LANGUAGE_MAX_LENGTH } from './locale-preferences';
import {
  NOTIFICATION_TYPE_NAMES,
  notificationTypeSchema,
  type NotificationType,
} from './notification';

export const INTERFACE_SOURCE_LOCALE = 'en-GB';
export const DEFAULT_INTERFACE_LANGUAGE = 'en';
export const INTERFACE_CATALOGUE_VERSION = 'b80-interface-language-1';

export const SUPPORTED_INTERFACE_LOCALES = [INTERFACE_SOURCE_LOCALE] as const;

export const interfaceLocaleTagSchema = z.enum(SUPPORTED_INTERFACE_LOCALES);
export type InterfaceLocaleTag = z.infer<typeof interfaceLocaleTagSchema>;

export const interfaceTextDirectionSchema = z.enum(['ltr', 'rtl']);
export type InterfaceTextDirection = z.infer<typeof interfaceTextDirectionSchema>;

export const INTERFACE_LANGUAGE_ALIASES = {
  en: INTERFACE_SOURCE_LOCALE,
} as const satisfies Record<string, InterfaceLocaleTag>;

export const interfaceLocaleMetadataSchema = z.strictObject({
  locale: interfaceLocaleTagSchema,
  displayName: z.string().min(1),
  textDirection: interfaceTextDirectionSchema,
  fallbackLocales: z.array(interfaceLocaleTagSchema),
  source: z.boolean(),
});

export type InterfaceLocaleMetadata = z.infer<typeof interfaceLocaleMetadataSchema>;

export const INTERFACE_LOCALE_METADATA = {
  [INTERFACE_SOURCE_LOCALE]: {
    locale: INTERFACE_SOURCE_LOCALE,
    displayName: 'English (United Kingdom)',
    textDirection: 'ltr',
    fallbackLocales: [],
    source: true,
  },
} as const satisfies Record<InterfaceLocaleTag, InterfaceLocaleMetadata>;

export const interfaceMessageSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('text'),
    value: z.string().min(1),
  }),
  z.strictObject({
    kind: z.literal('plural'),
    forms: z.strictObject({
      one: z.string().min(1),
      other: z.string().min(1),
    }),
  }),
]);

export type InterfaceMessage = z.infer<typeof interfaceMessageSchema>;

export const interfaceActivityTemplateKeySchema = z.enum(ACTIVITY_TEMPLATE_KEYS);
export const interfaceActivityTemplatesSchema = z.record(
  interfaceActivityTemplateKeySchema,
  z.string().min(1),
);

export const notificationTemplateCatalogueSchema = z.strictObject({
  version: z.string().min(1),
  templates: z.record(notificationTypeSchema, interfaceMessageSchema),
  sourceEventOverrides: z.record(z.string().min(1), interfaceMessageSchema),
});

export type NotificationTemplateCatalogue = z.infer<
  typeof notificationTemplateCatalogueSchema
>;

export const interfaceLanguageCatalogueResponseSchema = z.strictObject({
  requestedLanguage: z.string().max(LOCALE_LANGUAGE_MAX_LENGTH),
  resolvedLocale: interfaceLocaleTagSchema,
  sourceLocale: z.literal(INTERFACE_SOURCE_LOCALE),
  textDirection: interfaceTextDirectionSchema,
  fallbackLocales: z.array(interfaceLocaleTagSchema),
  version: z.string().min(1),
  messages: z.record(z.string().min(1), interfaceMessageSchema),
  activityTemplates: interfaceActivityTemplatesSchema,
  notificationTemplates: notificationTemplateCatalogueSchema,
});

export type InterfaceLanguageCatalogueResponse = z.infer<
  typeof interfaceLanguageCatalogueResponseSchema
>;

interface InterfaceCatalogue {
  version: string;
  messages: Record<string, InterfaceMessage>;
  activityTemplates: Record<ActivityTemplateKey, string>;
  notificationTemplates: NotificationTemplateCatalogue;
}

export const NOTIFICATION_INTERFACE_TEMPLATES = {
  mentioned: { kind: 'text', value: '{actor} mentioned you on {card}' },
  added_to_card: { kind: 'text', value: '{actor} added you to {card}' },
  removed_from_card: { kind: 'text', value: '{actor} removed you from {card}' },
  added_to_board: { kind: 'text', value: '{actor} added you to the board {board}' },
  removed_from_board: {
    kind: 'text',
    value: '{actor} removed you from the board {board}',
  },
  made_board_admin: {
    kind: 'text',
    value: '{actor} made you an admin of the board {board}',
  },
  board_closed: { kind: 'text', value: '{actor} closed the board {board}' },
  added_to_workspace: {
    kind: 'text',
    value: '{actor} added you to the workspace {workspace}',
  },
  made_workspace_admin: {
    kind: 'text',
    value: '{actor} made you an admin of the workspace {workspace}',
  },
  checklist_item_assigned: {
    kind: 'text',
    value: '{actor} assigned you a checklist item on {card}',
  },
  checklist_item_due_changed: {
    kind: 'text',
    value: 'A checklist item on {card} has a due-date update',
  },
  due_soon: { kind: 'text', value: '{card} is due soon' },
  watched_comment_added: { kind: 'text', value: '{actor} commented on {card}' },
  watched_due_date_changed: {
    kind: 'text',
    value: '{actor} changed the due date on {card}',
  },
  watched_completion_toggled: {
    kind: 'text',
    value: '{actor} changed whether {card} is complete',
  },
  watched_attachment_added: {
    kind: 'text',
    value: '{actor} attached a file to {card}',
  },
  watched_card_moved: { kind: 'text', value: '{actor} moved {card}' },
  watched_card_archived: { kind: 'text', value: '{actor} archived {card}' },
  watched_card_created_in_list: {
    kind: 'text',
    value: '{actor} added {card} to a list you are watching',
  },
  watched_card_created_on_board: {
    kind: 'text',
    value: '{actor} added {card} to a board you are watching',
  },
  /**
   * `FR-AUTO-034`. ⚠ **The first template in this table that fills NO slot,
   * and it must stay that way.** None of the four the renderer supplies
   * (`{actor}`, `{card}`, `{board}`, `{workspace}`) has a truthful value on
   * this row: the sweep noticed the failure, so there is no actor, and a
   * change registration is not a card, a board or a workspace even when its
   * subject is one. A template naming a slot the row cannot fill prints the
   * literal `{board}` into somebody's inbox — `render.ts`'s `renderSentence`
   * leaves nothing behind, so an unfilled slot is not dropped.
   *
   * The wording says *deliveries*, not *webhook*: the product's noun is
   * "change registration" and the corpus never uses "webhook" (`CLAUDE.md`,
   * and `docs/notes/webhook-events-plan.md` on why a grep for it reads as
   * absence). It also says what to do, because the notification is the only
   * place the member learns that re-enabling is possible at all.
   */
  change_registration_failing: {
    kind: 'text',
    value:
      'Deliveries to one of your change registrations keep failing, so it has been paused. Re-enable it once the endpoint is healthy.',
  },
} as const satisfies Record<NotificationType, InterfaceMessage>;

export const NOTIFICATION_SOURCE_EVENT_TEMPLATE_OVERRIDES = {
  'card.unarchived': { kind: 'text', value: '{actor} restored {card}' },
} as const satisfies Partial<Record<EventType, InterfaceMessage>>;

export const INTERFACE_CATALOGUES = {
  [INTERFACE_SOURCE_LOCALE]: {
    version: INTERFACE_CATALOGUE_VERSION,
    messages: {},
    activityTemplates: ACTIVITY_TEMPLATES,
    notificationTemplates: {
      version: INTERFACE_CATALOGUE_VERSION,
      templates: NOTIFICATION_INTERFACE_TEMPLATES,
      sourceEventOverrides: NOTIFICATION_SOURCE_EVENT_TEMPLATE_OVERRIDES,
    },
  },
} as const satisfies Record<InterfaceLocaleTag, InterfaceCatalogue>;

export type InterfaceLanguageResolution = Pick<
  InterfaceLanguageCatalogueResponse,
  | 'requestedLanguage'
  | 'resolvedLocale'
  | 'sourceLocale'
  | 'textDirection'
  | 'fallbackLocales'
>;

export function resolveInterfaceLanguage(
  storedLanguage: unknown = DEFAULT_INTERFACE_LANGUAGE,
): InterfaceLanguageResolution {
  const requestedLanguage = requestedLanguageForResponse(storedLanguage);
  const resolvedLocale = resolveInterfaceLocale(requestedLanguage);
  const metadata = INTERFACE_LOCALE_METADATA[resolvedLocale];

  return {
    requestedLanguage,
    resolvedLocale,
    sourceLocale: INTERFACE_SOURCE_LOCALE,
    textDirection: metadata.textDirection,
    fallbackLocales: [...metadata.fallbackLocales],
  };
}

export function getInterfaceLanguageCatalogue(
  storedLanguage: unknown = DEFAULT_INTERFACE_LANGUAGE,
): InterfaceLanguageCatalogueResponse {
  const resolution = resolveInterfaceLanguage(storedLanguage);
  const catalogue = INTERFACE_CATALOGUES[resolution.resolvedLocale];

  return interfaceLanguageCatalogueResponseSchema.parse({
    ...resolution,
    version: catalogue.version,
    messages: { ...catalogue.messages },
    activityTemplates: { ...catalogue.activityTemplates },
    notificationTemplates: {
      version: catalogue.notificationTemplates.version,
      templates: { ...catalogue.notificationTemplates.templates },
      sourceEventOverrides: {
        ...catalogue.notificationTemplates.sourceEventOverrides,
      },
    },
  });
}

export function notificationTemplateForInterfaceLanguage(
  storedLanguage: unknown,
  type: NotificationType,
  sourceEventType: string | null | undefined,
): string {
  const catalogue = getInterfaceLanguageCatalogue(storedLanguage).notificationTemplates;
  const override =
    typeof sourceEventType === 'string'
      ? catalogue.sourceEventOverrides[sourceEventType]
      : undefined;
  const template = override ?? catalogue.templates[type];
  return textMessageValue(template);
}

function requestedLanguageForResponse(storedLanguage: unknown): string {
  if (
    typeof storedLanguage === 'string' &&
    storedLanguage.length >= 1 &&
    storedLanguage.length <= LOCALE_LANGUAGE_MAX_LENGTH
  ) {
    return storedLanguage;
  }

  return DEFAULT_INTERFACE_LANGUAGE;
}

function resolveInterfaceLocale(requestedLanguage: string): InterfaceLocaleTag {
  const supportedLocale = interfaceLocaleTagSchema.safeParse(requestedLanguage);
  if (supportedLocale.success) {
    return supportedLocale.data;
  }

  if (Object.hasOwn(INTERFACE_LANGUAGE_ALIASES, requestedLanguage)) {
    return INTERFACE_LANGUAGE_ALIASES[
      requestedLanguage as keyof typeof INTERFACE_LANGUAGE_ALIASES
    ];
  }

  return INTERFACE_SOURCE_LOCALE;
}

function textMessageValue(message: InterfaceMessage): string {
  if (message.kind === 'text') return message.value;
  throw new RangeError('Notification templates must be text messages');
}

for (const type of NOTIFICATION_TYPE_NAMES) {
  if (NOTIFICATION_INTERFACE_TEMPLATES[type].kind !== 'text') {
    throw new RangeError(`Notification template "${type}" must be text`);
  }
}
